/**
 * Manchester Triage System - Presenting Complaints
 * 
 * This file contains definitions for the standard presenting complaints
 * used in the Manchester Triage System (MTS) version 3.
 * 
 * Each presenting complaint is a category of symptoms or conditions that
 * leads to specific triage pathways and discriminators.
 * 
 * Reference: Manchester Triage Group. Emergency Triage: Manchester Triage Group.
 * 3rd ed. Wiley-Blackwell; 2014.
 */

import { MTSPresentation } from '@/types/triage';

/**
 * MTS Presentations - Core list of presenting complaints
 */
export const MTS_PRESENTATIONS: MTSPresentation[] = [
  // General presentations
  {
    id: 'MTS-PRESENTATION-01',
    name: 'Abdominal Pain in Adults',
    description: 'Pain or discomfort in the abdomen in patients aged 16 or over',
    discriminators: [] // Will be populated from discriminators.ts
  },
  {
    id: 'MTS-PRESENTATION-02',
    name: 'Abdominal Pain in Children',
    description: 'Pain or discomfort in the abdomen in patients under 16',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-03',
    name: 'Asthma',
    description: 'Presentation with known asthma or symptoms suggestive of asthma',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-04',
    name: 'Shortness of Breath in Adults',
    description: 'Difficulty breathing or breathlessness in patients aged 16 or over',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-05',
    name: 'Shortness of Breath in Children',
    description: 'Difficulty breathing or breathlessness in patients under 16',
    discriminators: []
  },
  
  // Traumatic presentations
  {
    id: 'MTS-PRESENTATION-06',
    name: 'Major Trauma',
    description: 'Significant injury involving one or more body systems',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-07',
    name: 'Head Injury',
    description: 'Injury to the head, including scalp, skull and brain',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-08',
    name: 'Limb Problems',
    description: 'Pain, swelling, deformity or loss of function in limbs',
    discriminators: []
  },
  
  // Neurological presentations
  {
    id: 'MTS-PRESENTATION-09',
    name: 'Headache',
    description: 'Pain or discomfort in the head',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-10',
    name: 'Fits',
    description: 'Active seizure or history of recent seizure',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-11',
    name: 'Collapsed Adult',
    description: 'Loss of consciousness or altered level of consciousness in patients aged 16 or over',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-12',
    name: 'Collapsed Child',
    description: 'Loss of consciousness or altered level of consciousness in patients under 16',
    discriminators: []
  },
  
  // Cardiovascular presentations
  {
    id: 'MTS-PRESENTATION-13',
    name: 'Chest Pain',
    description: 'Pain or discomfort in the chest area',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-14',
    name: 'Palpitations',
    description: 'Awareness of heart beating, racing or skipping beats',
    discriminators: []
  },
  
  // Special groups
  {
    id: 'MTS-PRESENTATION-15',
    name: 'Crying Baby',
    description: 'Baby less than 1 year old who is crying or irritable',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-16',
    name: 'Unwell Child',
    description: 'Child who is generally unwell, without specific symptoms',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-17',
    name: 'Mental Health Problem',
    description: 'Presenting with psychological or psychiatric symptoms',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-18',
    name: 'Self-Harm',
    description: 'Deliberate self-harm or attempted suicide',
    discriminators: []
  },
  
  // Other common presentations
  {
    id: 'MTS-PRESENTATION-19',
    name: 'Sore Throat',
    description: 'Pain or discomfort in the throat',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-20',
    name: 'Ear Problems',
    description: 'Pain, discharge or hearing problems in the ear',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-21',
    name: 'Eye Problems',
    description: 'Pain, redness, visual disturbance or injury to the eye',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-22',
    name: 'Rashes',
    description: 'Skin eruption or discoloration',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-23',
    name: 'Fever',
    description: 'Elevated body temperature',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-24',
    name: 'Vomiting',
    description: 'Forceful expulsion of stomach contents',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-25',
    name: 'Diarrhea',
    description: 'Loose or watery stools',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-26',
    name: 'Gastrointestinal Bleeding',
    description: 'Bleeding from the digestive tract',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-27',
    name: 'Urinary Problems',
    description: 'Pain, frequency, urgency or other problems with urination',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-28',
    name: 'Vaginal Bleeding',
    description: 'Bleeding from the vagina, including in pregnancy',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-29',
    name: 'Wound',
    description: 'Open injury to the skin and underlying tissues',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-30',
    name: 'Dental Problems',
    description: 'Pain, injury or infection involving the teeth or gums',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-31',
    name: 'Local Infection',
    description: 'Infection confined to a specific area of the body',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-32',
    name: 'General Weakness',
    description: 'Overall feeling of reduced strength or energy',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-33',
    name: 'Overdose and Poisoning',
    description: 'Ingestion of drugs or other toxic substances in excess',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-34',
    name: 'Allergic Reaction',
    description: 'Hypersensitivity response to a substance',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-35',
    name: 'Foreign Body',
    description: 'External object in a body cavity or embedded in tissue',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-36',
    name: 'Burns and Scalds',
    description: 'Tissue damage from heat, chemicals, electricity or radiation',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-37',
    name: 'Neck Pain',
    description: 'Pain or stiffness in the neck region',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-38',
    name: 'Back Pain',
    description: 'Pain in the back, from the neck to the tailbone',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-39',
    name: 'Facial Problems',
    description: 'Pain, swelling or injury to the face',
    discriminators: []
  },
  {
    id: 'MTS-PRESENTATION-40',
    name: 'Major Incident: Primary Triage',
    description: 'Initial triage in a major incident situation',
    discriminators: []
  }
];

/**
 * Helper function to find a presentation by ID
 */
export function findPresentationById(id: string): MTSPresentation | undefined {
  return MTS_PRESENTATIONS.find(presentation => presentation.id === id);
}

/**
 * Helper function to find a presentation by name (case-insensitive)
 */
export function findPresentationByName(name: string): MTSPresentation | undefined {
  return MTS_PRESENTATIONS.find(
    presentation => presentation.name.toLowerCase() === name.toLowerCase()
  );
}

/**
 * Return presentations applicable for a patient based on age, gender, etc.
 */
export function getApplicablePresentations(patientData: { 
  age?: number; 
  gender?: string 
}): MTSPresentation[] {
  const { age, gender } = patientData;
  
  return MTS_PRESENTATIONS.filter(presentation => {
    // Filter out adult-specific presentations for children
    if (age !== undefined && age < 16 && presentation.name.includes('Adult')) {
      return false;
    }
    
    // Filter out child-specific presentations for adults
    if (age !== undefined && age >= 16 && presentation.name.includes('Child')) {
      return false;
    }
    
    // Filter out gender-specific presentations
    if (gender === 'male' && presentation.name.includes('Vaginal')) {
      return false;
    }
    
    return true;
  });
}
